import * as angular from 'angular';
import { IMetadataValidationService, IUtilities } from '../../typings/services';
import { IClInputContainerAttributes } from '../typings/directives';

/**
 * cl-input-container is a wrapper for md-input-container (other files in this module are wrappers for other md- or ng- directives )
 * In the event that angular-material is no longer desired to be used in the project,
 * we can edit these directives to propagate site-wide changes
 * This is also used to help apply metadata-based validation to the directive's contents
 *
 * Anything that you place inside of a cl-input-container must follow its rules.
 * We are using angular's inner workings to our advantage to allow the contents to be converted into uncompiled md- and ng- directives,
 * gather them here and then compile all at once.
 * ONLY those directives which require this directive's controller are allowed to be used inside a cl-input-container
 * Refer to other directives within this module for valid directives.
 * If you need to implement an input directive that is not currently utilized, please follow those directives as an example.
 * If you must include a directive that was not designed for this feature, please wrap it in a cl-extras directive. (example: md-tooltip)
 */

export class ClInputContainerDirective implements angular.IDirective {
    public static $name: string = 'clInputContainer';
    public controller = ClInputContainerController;
    public restrict: string = 'E';

    public link = (
        scope: any,
        element: angular.IAugmentedJQuery,
        attrs: IClInputContainerAttributes,
        controller: ClInputContainerController) => {
        const container = angular.element('<md-input-container>');

        /**
         * We don't want angular-material layout-attributes
         * (as having them on both the component & the generated input will cause display issues)
         * nor do we want ng-if because double watcher,
         * nor the attrs explicity used by this directive because why bother
         */
        this.utilities.copyAllAttributesWithExceptions(container, attrs, [
            { name: 'cl-validation' },
            { name: 'form-name' },
            { name: 'flex', method: 'startsWith' },
            { name: 'layout', method: 'startsWith' },
            { name: 'ng', method: 'startsWith' }
        ]);

        const validations = this.$parse(attrs.clValidation)(scope);

        /**
         * Build the label, including validations if present (ex. 'required' asterisk).
         */
        const label = controller.label;

        if (!this.lodash.isUndefined(label)) {
            const validatedLabel = this.metadataValidationService.generateLabel(scope, label, validations, attrs.formName);
            container.append(validatedLabel);
        }

        /**
         * 'input' can be any type of input -- input, textarea, select...
         *  this will have been added in the link phase of a child input directive (cl-input, cl-select, etc...)
         */
        const input = controller.input;
        const messages = this.metadataValidationService.generateValidation(scope, input, validations, attrs.formName);
        const validatedInput = input.add(messages);
        container.append(validatedInput);

        /* If there are extras, add them */
        container.append(controller.extras);

        /* Order is important! */
        element.append(container);
        this.$compile(container)(scope);
    }

    constructor(
        private $compile: angular.ICompileService,
        private $parse: angular.IParseService,
        private lodash: _.LoDashStatic,
        private metadataValidationService: IMetadataValidationService,
        private utilities: IUtilities
    ) {
    }

    public static Factory() {
        const directive = ($compile, $parse, lodash, metadataValidationService, utilities) => {
            return new ClInputContainerDirective($compile, $parse, lodash, metadataValidationService, utilities);
        };

        directive['$inject'] = [
            '$compile',
            '$parse',
            'lodash',
            'metadataValidationService',
            'utilities'
        ];

        return directive;
    }
}

// tslint:disable-next-line:max-classes-per-file
export class ClInputContainerController implements angular.IController {
    public label: angular.IAugmentedJQuery;
    public input: angular.IAugmentedJQuery;
    public extras: angular.IAugmentedJQuery;
}
